<template>
  <StatsWidget
    :title="$t('stats_dashboard.leaderboard.title')"
    icon="users"
    tone="amber"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasAnyEntry"
    :span-full="true"
    @refresh="$emit('refresh')"
  >
    <div class="stats-leaderboard">
      <section
        v-for="board in boards"
        :key="board.key"
        class="stats-leaderboard__panel"
      >
        <header class="stats-leaderboard__panel-head">
          <h4 class="stats-leaderboard__panel-title">{{ board.title }}</h4>
          <p class="stats-leaderboard__panel-description">
            {{ board.description }}
          </p>
        </header>

        <ul v-if="board.rows.length" class="stats-leaderboard__list">
          <li
            v-for="(row, idx) in board.rows"
            :key="row.user_id || row.name"
            class="stats-leaderboard__row"
          >
            <span
              class="stats-leaderboard__rank"
              :class="rankClass(idx)"
            >{{ idx + 1 }}</span>
            <span class="stats-leaderboard__name">{{ row.name }}</span>
            <div class="stats-leaderboard__metrics">
              <span class="stats-leaderboard__count">
                {{ board.formatCount(row.count) }}
              </span>
              <span class="stats-leaderboard__value">
                {{ formatCurrency(row.value) }}
              </span>
            </div>
          </li>
        </ul>

        <p v-else class="stats-leaderboard__empty">
          {{ $t('stats_dashboard.leaderboard.empty') }}
        </p>
      </section>
    </div>
  </StatsWidget>
</template>

<script>
  /**
   * Leaderboard (§9). Two side-by-side ranked lists for sellers
   * (accepted proposals) and installers (completed work orders).
   * Each row shows rank, name, count + value. The top-3 ranks get a
   * subtle medal-ish color so the eye lands on them first.
   */
  import { computed, getCurrentInstance } from 'vue';
  import StatsWidget from './StatsWidget.vue';
  import { formatCurrency, formatInteger } from '@/utils/statsFormat';

  export default {
    name: 'StatsLeaderboard',

    components: { StatsWidget },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const sellers = computed(() => props.widget.data?.sellers || []);
      const installers = computed(() => props.widget.data?.installers || []);

      const hasAnyEntry = computed(
        () => sellers.value.length > 0 || installers.value.length > 0
      );

      const boards = computed(() => [
        {
          key: 'sellers',
          title: $t('stats_dashboard.leaderboard.sellers_title'),
          description: $t('stats_dashboard.leaderboard.sellers_description'),
          rows: sellers.value,
          formatCount: (n) =>
            $t('stats_dashboard.leaderboard.count_proposals', {
              n: formatInteger(n),
            }),
        },
        {
          key: 'installers',
          title: $t('stats_dashboard.leaderboard.installers_title'),
          description: $t('stats_dashboard.leaderboard.installers_description'),
          rows: installers.value,
          formatCount: (n) =>
            $t('stats_dashboard.leaderboard.count_orders', {
              n: formatInteger(n),
            }),
        },
      ]);

      const rankClass = (idx) => {
        if (idx === 0) return 'stats-leaderboard__rank--gold';
        if (idx === 1) return 'stats-leaderboard__rank--silver';
        if (idx === 2) return 'stats-leaderboard__rank--bronze';
        return '';
      };

      return {
        boards,
        hasAnyEntry,
        rankClass,
        formatCurrency,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-leaderboard {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;

    @include lg-down {
      grid-template-columns: 1fr;
    }

    &__panel {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      min-width: 0;
    }

    &__panel-head {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }

    &__panel-title {
      margin: 0;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-700);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    &__panel-description {
      margin: 0;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__row {
      display: grid;
      grid-template-columns: 1.75rem 1fr auto;
      align-items: center;
      gap: 0.625rem;
      padding: 0.5rem 0.5rem;
      border-radius: 0.5rem;
      transition: background-color 0.15s ease;

      @include hover {
        background: var(--slate-50);
      }
    }

    &__rank {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 50%;
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--slate-600);
      background: var(--slate-100);
      font-variant-numeric: tabular-nums;
      flex-shrink: 0;

      &--gold {
        color: #b45309; // amber-700
        background: linear-gradient(135deg, #fef3c7, #fcd34d);
      }
      &--silver {
        color: #475569; // slate-600
        background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
      }
      &--bronze {
        color: #9a3412; // orange-800
        background: linear-gradient(135deg, #fed7aa, #fdba74);
      }
    }

    &__name {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-800);
      @include ellipsis(1);
    }

    &__metrics {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.0625rem;
      flex-shrink: 0;
    }

    &__count {
      font-size: 0.6875rem;
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;
    }

    &__value {
      font-size: 0.875rem;
      font-weight: 700;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
    }

    &__empty {
      margin: 0;
      padding: 1rem;
      font-size: 0.8125rem;
      color: var(--slate-500);
      text-align: center;
      background: var(--slate-50);
      border: 1px dashed var(--slate-200);
      border-radius: 0.5rem;
    }
  }
</style>
